import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, Lock, User, Phone, Tag, AlertCircle, Eye, EyeOff, X, Info, Lock as LockIcon, FileText } from 'lucide-react';
import useAuthStore from '../../store/authStore';

const registerSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string()
    .matches(/^[0-9]{11}$/, 'Phone must be 11 digits')
    .required('Phone number is required'),
  password: yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  businessTags: yup.string(),
  agreeToTerms: yup.boolean()
    .oneOf([true], 'You must agree to the terms and privacy policy')
    .required('You must agree to the terms and privacy policy'),
});

const businessTagsOptions = [
  'Tech',
  'Logistics',
  'Retail',
  'E-commerce',
  'Services',
  'Manufacturing',
  'Agriculture',
  'Healthcare',
  'Education',
];

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeModal, setActiveModal] = useState(null);
  const navigate = useNavigate();
  const { register: registerUser, isLoading, error } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      agreeToTerms: false
    }
  });

  const agreeToTerms = watch('agreeToTerms');

  const toggleTag = (tag) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(newTags);
    setValue('businessTags', newTags.join(','));
  };

  const openModal = (modal) => setActiveModal(modal);
  const closeModal = () => setActiveModal(null);

  const onSubmit = async (data, e) => {
    e?.preventDefault();
    try {
      const userData = {
        email: data.email,
        phone: data.phone,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        businessTags: selectedTags,
      };
      
      await registerUser(userData);
      
      // Navigate to login page instead of verify-phone
      navigate('/login', { 
        state: { message: 'Registration successful! Please login with your credentials.' }
      });
      
    } catch (error) {
      console.error('Registration error:', error);
      // The error will be shown by the store via toast
      // The error message is already handled by the store
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center mb-2">
          <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
          <h2 className="text-xl font-bold text-gray-800">Create your account</h2>
        </div>
        <p className="text-sm text-gray-500 ml-3">
          Or{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-700">
            sign in to your existing account
          </Link>
        </p>
      </div>

      {error && (
        <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
          <div className="flex items-start">
            <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
              <AlertCircle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h3 className="text-xs font-medium text-red-800">Error</h3>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-xs font-medium text-gray-600 mb-1.5">
              First Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="firstName"
                type="text"
                autoComplete="given-name"
                {...register('firstName')}
                className={`block w-full pl-9 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                  errors.firstName ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                }`}
                placeholder="John"
              />
            </div>
            {errors.firstName && (
              <p className="mt-1.5 text-xs text-red-600">{errors.firstName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-xs font-medium text-gray-600 mb-1.5">
              Last Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="lastName"
                type="text"
                autoComplete="family-name"
                {...register('lastName')}
                className={`block w-full pl-9 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                  errors.lastName ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                }`}
                placeholder="Doe"
              />
            </div>
            {errors.lastName && (
              <p className="mt-1.5 text-xs text-red-600">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <input
              id="email"
              type="email"
              autoComplete="email"
              {...register('email')}
              className={`block w-full pl-9 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                errors.email ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
              }`}
              placeholder="you@example.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-xs font-medium text-gray-600 mb-1.5">
            Phone Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone className="h-4 w-4 text-gray-400" />
            </div>
            <input
              id="phone"
              type="tel"
              autoComplete="tel"
              {...register('phone')}
              className={`block w-full pl-9 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                errors.phone ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
              }`}
              placeholder="08012345678"
            />
          </div>
          <p className="mt-1.5 text-xs text-gray-500">11-digit Nigerian number</p>
          {errors.phone && (
            <p className="mt-1.5 text-xs text-red-600">{errors.phone.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1.5">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...register('password')}
                className={`block w-full pl-9 pr-10 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                  errors.password ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                }`}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1.5 text-xs text-red-600">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-600 mb-1.5">
              Confirm Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...register('confirmPassword')}
                className={`block w-full pl-9 pr-10 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                  errors.confirmPassword ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                }`}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1.5 text-xs text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Business Interests (Optional)
          </label>
          <div className="flex flex-wrap gap-2">
            {businessTagsOptions.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`
                  inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all border
                  ${selectedTags.includes(tag)
                    ? 'bg-primary-50 text-primary-700 border-primary-200 shadow-neomorph-inset'
                    : 'bg-white text-gray-700 border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
                  }
                `}
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">Select tags that match your business interests</p>
        </div>

        <div className="space-y-2">
          <label className="flex items-start">
            <input
              type="checkbox"
              {...register('agreeToTerms')}
              className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded shadow-neomorph-inset"
            />
            <span className="ml-2 text-xs text-gray-600">
              I agree to the{' '}
              <button
                type="button"
                onClick={() => openModal('terms')}
                className="font-medium text-primary-600 hover:text-primary-700 underline"
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => openModal('privacy')}
                className="font-medium text-primary-600 hover:text-primary-700 underline"
              >
                Privacy Policy
              </button>
            </span>
          </label>
          {errors.agreeToTerms && (
            <p className="text-xs text-red-600">{errors.agreeToTerms.message}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading || !agreeToTerms}
            className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-3 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating account...
              </div>
            ) : (
              'Create Account'
            )}
          </button>
        </div>
      </form>

      {/* Terms Modal */}
      {activeModal === 'terms' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-neomorph max-w-lg w-full max-h-[80vh] overflow-y-auto border border-primary-100/30">
            <div className="sticky top-0 bg-white border-b border-primary-100/50 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-800">Terms of Service</h3>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                By using Fastel, you agree to these terms. Please read them carefully.
              </p>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Account Responsibility</h4>
                  <p className="text-xs text-gray-600">You're responsible for maintaining account security and all activities under your account.</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Transactions</h4>
                  <p className="text-xs text-gray-600">All transactions are final. Refunds are processed according to our refund policy.</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Prohibited Activities</h4>
                  <p className="text-xs text-gray-600">Fraudulent transactions, spam, and abuse of our platform are strictly prohibited.</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Service Availability</h4>
                  <p className="text-xs text-gray-600">We strive for 99.9% uptime but cannot guarantee uninterrupted service due to maintenance or technical issues.</p>
                </div>
              </div>
              <div className="bg-primary-50 rounded-lg p-4 shadow-neomorph-inset">
                <p className="text-xs text-gray-600">
                  By checking "I agree" on the registration form, you acknowledge that you have read and understood these terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {activeModal === 'privacy' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-neomorph max-w-lg w-full max-h-[80vh] overflow-y-auto border border-primary-100/30">
            <div className="sticky top-0 bg-white border-b border-primary-100/50 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <LockIcon className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-800">Privacy Policy</h3>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                At Fastel, we take your privacy seriously. This policy describes how we collect, use, and protect your data.
              </p>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Data Collection</h4>
                  <p className="text-xs text-gray-600">We collect only essential information needed to provide our services: name, email, phone number, and transaction details.</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Data Protection</h4>
                  <p className="text-xs text-gray-600">256-bit encryption protects all your data. We never share your personal information with third parties without your consent.</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Your Rights</h4>
                  <p className="text-xs text-gray-600">You can request data deletion or export at any time through your settings. Contact support for assistance.</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Cookies</h4>
                  <p className="text-xs text-gray-600">We use essential cookies to maintain your session and improve your experience. You can disable them in your browser settings.</p>
                </div>
              </div>
              <div className="bg-primary-50 rounded-lg p-4 shadow-neomorph-inset">
                <p className="text-xs text-gray-600">
                  For questions about your privacy, contact us at privacy@fastel.ng
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;